"use client";

import React, { useState, useCallback, useMemo } from "react";
import { Contact } from "@/lib/types/database";
import { Button } from "@west-tennessee-consulting/ui";
import { Mail, Plus, Trash2, AlertCircle } from "lucide-react";
import { useUpdateContact } from "@/features/contacts/hooks";
import { toast } from "sonner";

interface ContactEmailsDisplayProps {
  contact: Contact;
  onUpdate?: (updatedContact: Contact) => void;
}

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export function ContactEmailsDisplay({ contact, onUpdate }: ContactEmailsDisplayProps) {
  const [newAlias, setNewAlias] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const updateContact = useUpdateContact();

  // Memoize aliases array
  const aliases = useMemo(() => {
    if (!contact.email_aliases) return [];
    // Filter out null values from Maybe<string> array
    return contact.email_aliases.filter((alias): alias is string => alias != null);
  }, [contact.email_aliases]);

  // Validate email format
  const validateEmail = useCallback((email: string): boolean => {
    return EMAIL_REGEX.test(email);
  }, []);

  // Handle adding new alias
  const handleAddAlias = useCallback(async () => {
    const trimmedAlias = newAlias.trim();

    if (!trimmedAlias) {
      setValidationError("Email cannot be empty");
      return;
    }

    if (!validateEmail(trimmedAlias)) {
      setValidationError("Invalid email format");
      return;
    }

    // Check for duplicates
    if (trimmedAlias.toLowerCase() === contact.email.toLowerCase()) {
      setValidationError("This is already the primary email");
      return;
    }

    if (aliases.some((a) => a.toLowerCase() === trimmedAlias.toLowerCase())) {
      setValidationError("This email is already in the list");
      return;
    }

    setIsAdding(true);
    setValidationError(null);

    try {
      const updatedAliases = [...aliases, trimmedAlias];
      await updateContact.mutateAsync({
        id: contact.id,
        updates: { email_aliases: updatedAliases },
      });

      setNewAlias("");
      toast.success("Email alias added successfully");
      onUpdate?.({ ...contact, email_aliases: updatedAliases });
    } catch (error) {
      console.error("Error adding email alias:", error);
      toast.error("Failed to add email alias");
      setValidationError("Failed to add email alias");
    } finally {
      setIsAdding(false);
    }
  }, [newAlias, contact, aliases, validateEmail, updateContact, onUpdate]);

  // Handle removing alias
  const handleRemoveAlias = useCallback(
    async (aliasToRemove: string) => {
      try {
        const updatedAliases = aliases.filter((a) => a !== aliasToRemove);
        await updateContact.mutateAsync({
          id: contact.id,
          updates: { email_aliases: updatedAliases },
        });

        toast.success("Email alias removed successfully");
        onUpdate?.({ ...contact, email_aliases: updatedAliases });
      } catch (error) {
        console.error("Error removing email alias:", error);
        toast.error("Failed to remove email alias");
      }
    },
    [contact, aliases, updateContact, onUpdate]
  );

  // Handle key press in input
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        void handleAddAlias();
      }
    },
    [handleAddAlias]
  );

  return (
    <div className="space-y-4 p-4 md:space-y-6 md:p-6">
      {/* Primary Email Section */}
      <div>
        <h3 className="text-sm font-semibold text-card-foreground mb-3 flex items-center gap-2">
          <Mail className="h-4 w-4" />
          Primary Email
        </h3>
        <div className="bg-card-50 rounded-lg p-3 md:p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm font-medium text-card-foreground break-words md:break-normal">{contact.email}</p>
          <p className="text-xs text-gray-500 mt-1">
            This is the main email address for this contact
          </p>
        </div>
      </div>

      {/* Alternative Emails Section */}
      <div>
        <h3 className="text-sm font-semibold text-card-foreground mb-3">Alternative Emails</h3>

        {/* Aliases List */}
        {aliases.length > 0 ? (
          <div className="space-y-2 mb-4">
            {aliases.map((alias) => (
              <div
                key={alias}
                className="flex items-center justify-between bg-card-50 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
              >
                <p className="text-sm text-card-foreground break-words md:break-normal">{alias}</p>
                <button type="button"
                  onClick={() => handleRemoveAlias(alias)}
                  disabled={updateContact.isPending}
                  className="flex items-center justify-center h-11 w-11 md:h-auto md:w-auto text-red-600 hover:text-red-700 disabled:opacity-50"
                  title="Remove email"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 mb-4">No alternative emails added yet</p>
        )}

        {/* Add New Alias */}
        {aliases.length < 10 && (
          <div className="space-y-2">
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                type="email"
                value={newAlias}
                onChange={(e) => {
                  setNewAlias(e.target.value);
                  setValidationError(null);
                }}
                onKeyPress={handleKeyPress}
                placeholder="Enter alternative email address"
                disabled={isAdding || updateContact.isPending}
                className="flex-1 h-11 px-3 md:h-auto md:py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-card-100 dark:border-gray-700"
              />
              <button type="button"
                onClick={handleAddAlias}
                disabled={isAdding || updateContact.isPending || !newAlias.trim()}
                className="h-11 px-3 md:h-auto md:py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-card-300 disabled:cursor-not-allowed flex items-center justify-center md:justify-start gap-1"
              >
                <Plus className="h-4 w-4" />
                Add
              </button>
            </div>

            {/* Validation Error */}
            {validationError && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle className="h-4 w-4" />
                {validationError}
              </div>
            )}

            {/* Helper Text */}
            <p className="text-xs text-gray-500">
              You can add up to 10 alternative email addresses
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
